import React, { createContext, useMemo } from "react";

import { some, stubFalse } from "lodash/fp";

import useAuth from "@app/common/auth/useAuth";
import {
  CALENDAR_PROVIDERS,
  INTEGRATION_PROVIDERS_BY_ID,
  PRODUCTIVITY_PROVIDERS,
} from "@app/common/constants/providers";
import type { IntegrationProviderId } from "@app/common/types";
import { canAccessFeature } from "@app/common/utils";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import Config from "@app/config";

export interface FeatureAccess {
  // feature access
  canAccessForYouPage: boolean;
  canAccessCoaching: boolean;
  canAccessIntegration: (providerId: IntegrationProviderId) => boolean;
  canAccessMeetingDownloads: boolean;
  canAccessPlayback: boolean;
  canAccessTeamMemberData: boolean;
  canAccessTeamRollupReports: boolean;
  canAccessTranscript: boolean;
  canAccessWorkspaceOverview: boolean;
  canAccessWorkspaceRollupReports: boolean;
  canAccessHomePage: boolean;
  // create access
  canCreateWorkspace: boolean;
  canCreateWorkspaceTeam: boolean;
  // delete access
  canDeleteWorkspace: boolean;
  // edit access
  canEditAffectiveMetricsEnabled: boolean;
  canEditBotConfig: boolean;
  canEditBotName: boolean;
  canEditBillingPlan: boolean;
  canEditInternalParticipantAccessLevel: boolean;
  canEditExternalParticipantAccessLevel: boolean;
  canEditSendMeetingPreRead: boolean;
  canEditSendMeetingRecap: boolean;
  canEditSendTopicReport: boolean;
  canEditShareLinkInBotMessage: boolean;
  canEditStoreRecordingOptIn: boolean;
  canEditTranscriptionOptIn: boolean;
  canEditWorkspaceSettings: boolean;
  // misc permission access
  canConnectCalendars: boolean;
  canConnectProductivityIntegrations: boolean;
  canInviteUsers: boolean;
}

const DEFAULT_FEATURE_ACCESS_CONTEXT: FeatureAccess = {
  // feature access
  canAccessCoaching: false,
  canAccessForYouPage: false,
  canAccessIntegration: stubFalse,
  canAccessMeetingDownloads: false,
  canAccessPlayback: false,
  canAccessTeamMemberData: false,
  canAccessTeamRollupReports: false,
  canAccessTranscript: false,
  canAccessWorkspaceOverview: false,
  canAccessWorkspaceRollupReports: false,
  canAccessHomePage: false,
  // create access
  canCreateWorkspace: false,
  canCreateWorkspaceTeam: false,
  // delete access
  canDeleteWorkspace: false,
  // edit access
  canEditAffectiveMetricsEnabled: false,
  canEditBotConfig: false,
  canEditBotName: false,
  canEditBillingPlan: false,
  canEditInternalParticipantAccessLevel: false,
  canEditExternalParticipantAccessLevel: false,
  canEditSendMeetingPreRead: false,
  canEditSendMeetingRecap: false,
  canEditSendTopicReport: false,
  canEditShareLinkInBotMessage: false,
  canEditStoreRecordingOptIn: false,
  canEditTranscriptionOptIn: false,
  canEditWorkspaceSettings: false,
  // misc permission access
  canConnectCalendars: false,
  canConnectProductivityIntegrations: false,
  canInviteUsers: false,
};

export const FeatureAccessContext = createContext<FeatureAccess>(
  DEFAULT_FEATURE_ACCESS_CONTEXT
);

export const FeatureAccessContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();
  const { inWorkspace } = useWorkspace();

  // TODO: use a different metadata field to indicate a test user
  const _isTestUser = user?.userMetadata?.emailProviderConnectEnabled ?? false;

  // General Feature Access
  const canAccessCoaching = canAccessFeature(user, {
    // TODO (ENG-6920) - use setting from user access permissions once that exists
    permission: true,
    allowNoUser: true,
  });

  const canAccessMeetingDownloads = canAccessFeature(user, {
    permission: user?.accessPermissions?.meetingDownloadEnabled,
  });

  const canAccessIntegration = (providerId: IntegrationProviderId) => {
    const providerConfig = INTEGRATION_PROVIDERS_BY_ID[providerId];
    return (
      canAccessFeature(user, {
        // if at least one of the settings is enabled, they can enable the integration
        permission: some(
          (setting) => user?.accessPermissions?.[setting] ?? true,
          providerConfig.accessSettings
        ),
      }) && !providerConfig.disabled?.()
    );
  };

  const canConnectCalendars = some(
    ({ id }) => canAccessIntegration(id),
    CALENDAR_PROVIDERS
  );

  const canConnectProductivityIntegrations = some(
    ({ id }) => canAccessIntegration(id),
    PRODUCTIVITY_PROVIDERS
  );

  // Meeting report-specific Access
  const canAccessTranscript = canAccessFeature(user, {
    permission: user?.accessPermissions?.transcripts,
    // if there isn't a user assume that they can access the transcript
    // since the "user" isn't in a workspace
    // TODO: better way to resolve workspace permissions for logged-out users
    allowNoUser: true,
  });

  const canAccessPlayback = canAccessFeature(user, {
    permission: user?.accessPermissions?.storeRecording,
    // if there isn't a user assume that they can access video playback
    // since the "user" isn't in a workspace
    // TODO: better way to resolve workspace permissions for logged-out users
    allowNoUser: true,
  });

  const canAccessForYouPage = canAccessFeature(user, {
    permission: user?.accessPermissions?.forYouPage,
  });

  const canAccessHomePage = canAccessFeature(user, {
    permission:
      // TODO: remove workspace check once home page is rolled out to all prod users
      //  or once backend has homepage added to accessPermissions
      Config.flags.enableHomepage ||
      user?.workspaceId === Config.readWorkspaceId,
  });

  // Workspace-specific Access
  const canAccessTeamRollupReports = canAccessFeature(user, {
    permission: inWorkspace && user?.accessPermissions?.teamRollups,
  });

  const canAccessWorkspaceRollupReports = canAccessFeature(user, {
    permission: inWorkspace && user?.accessPermissions?.workspaceRollups,
  });

  const canAccessTeamMemberData = canAccessFeature(user, {
    permission: inWorkspace && user?.accessPermissions?.shareTeamData,
  });

  const canAccessWorkspaceOverview = canAccessFeature(user, {
    permission: inWorkspace && user?.accessPermissions?.workspaceOverview,
  });

  const canEditWorkspaceSettings = canAccessFeature(user, {
    permission: inWorkspace && user?.editAccess?.workspaceSettings,
  });

  const canInviteUsers = canAccessFeature(user, {
    permission: user?.accessPermissions?.inviteUsers,
  });

  // Edit Access
  const canEditAffectiveMetricsEnabled = canAccessFeature(user, {
    permission: user?.editAccess?.affectiveMetricsEnabled,
  });

  const canEditStoreRecordingOptIn = canAccessFeature(user, {
    permission: user?.editAccess?.storeRecordingOptIn,
  });

  const canEditBotConfig = canAccessFeature(user, {
    permission: user?.editAccess?.botConfig,
  });

  const canEditBotName = canAccessFeature(user, {
    permission: user?.editAccess?.botName,
  });

  const canEditShareLinkInBotMessage = canAccessFeature(user, {
    permission: user?.editAccess?.shareLinkInBotMessage,
  });

  const canEditExternalParticipantAccessLevel = canAccessFeature(user, {
    permission: Boolean(user?.editAccess?.externalParticipantAccessLevel),
  });

  const canEditInternalParticipantAccessLevel = canAccessFeature(user, {
    permission: Boolean(user?.editAccess?.internalParticipantAccessLevel),
  });

  const canEditTranscriptionOptIn = canAccessFeature(user, {
    permission: user?.editAccess?.transcriptionOptIn,
  });

  const canEditBillingPlan = canAccessFeature(user, {
    permission: user?.editAccess?.billingPlan,
  });

  const canEditSendMeetingPreRead = canAccessFeature(user, {
    permission: user?.editAccess?.sendPreRead,
  });

  const canEditSendMeetingRecap = canAccessFeature(user, {
    permission: user?.editAccess?.sendMeetingRecap,
  });

  const canEditSendTopicReport = canAccessFeature(user, {
    permission: user?.editAccess?.sendTopicSummary,
  });

  // Create access
  const canCreateWorkspace = canAccessFeature(user, {
    permission: user?.createAccess?.workspace,
  });

  const canCreateWorkspaceTeam = canAccessFeature(user, {
    permission: inWorkspace && user?.createAccess?.workspaceTeam,
  });

  // Delete Access
  const canDeleteWorkspace = canAccessFeature(user, {
    permission: user?.deleteAccess?.workspace,
  });

  const access = useMemo(() => {
    return {
      canAccessCoaching,
      canAccessForYouPage,
      canAccessIntegration,
      canAccessMeetingDownloads,
      canAccessPlayback,
      canAccessTeamMemberData,
      canAccessTeamRollupReports,
      canAccessTranscript,
      canAccessWorkspaceOverview,
      canAccessWorkspaceRollupReports,
      canAccessHomePage,
      canCreateWorkspace,
      canCreateWorkspaceTeam,
      canDeleteWorkspace,
      canEditAffectiveMetricsEnabled,
      canEditBillingPlan,
      canEditBotConfig,
      canEditBotName,
      canEditInternalParticipantAccessLevel,
      canEditExternalParticipantAccessLevel,
      canEditSendMeetingPreRead,
      canEditSendMeetingRecap,
      canEditSendTopicReport,
      canEditShareLinkInBotMessage,
      canEditStoreRecordingOptIn,
      canEditTranscriptionOptIn,
      canEditWorkspaceSettings,
      canConnectCalendars,
      canConnectProductivityIntegrations,
      canInviteUsers,
    };
  }, [
    canAccessCoaching,
    canAccessForYouPage,
    canAccessIntegration,
    canAccessMeetingDownloads,
    canAccessPlayback,
    canAccessTeamMemberData,
    canAccessTeamRollupReports,
    canAccessTranscript,
    canAccessWorkspaceOverview,
    canAccessWorkspaceRollupReports,
    canAccessHomePage,
    canCreateWorkspace,
    canCreateWorkspaceTeam,
    canDeleteWorkspace,
    canEditAffectiveMetricsEnabled,
    canEditBillingPlan,
    canEditBotConfig,
    canEditInternalParticipantAccessLevel,
    canEditExternalParticipantAccessLevel,
    canEditSendMeetingPreRead,
    canEditSendMeetingRecap,
    canEditSendTopicReport,
    canEditShareLinkInBotMessage,
    canEditTranscriptionOptIn,
    canEditStoreRecordingOptIn,
    canEditTranscriptionOptIn,
    canEditWorkspaceSettings,
    canConnectCalendars,
    canConnectProductivityIntegrations,
    canInviteUsers,
  ]);

  return (
    <FeatureAccessContext.Provider value={access}>
      {children}
    </FeatureAccessContext.Provider>
  );
};
